/**
 * @fileoverview API client helpers for indirect cost subcategories.
 * @module features/indirect-costs/api/indirectCostSubcategoriesService
 */

import apiClient from '@/lib/apiClient'
import {
  pageIndirectCostSubcategoryResponseSchema,
  indirectCostSubcategorySchema,
  type IndirectCostSubcategoryDTO,
  type PageIndirectCostSubcategoryResponse,
} from '../schemas/indirectCostsSchema'

/**
 * Query parameters supported when listing indirect cost subcategories.
 */
export interface ListIndirectCostSubcategoriesParams {
  /** Optional category filter */
  category?: 'OPEX' | 'DA'
  /** Case-insensitive search applied to display name or slug */
  search?: string
  /** Zero-based page index for pagination */
  page?: number
  /** Page size (defaults to 200 to cover typical admin lists) */
  size?: number
}

/**
 * Fetches subcategories scoped to the current organization.
 *
 * @param params Optional filtering and pagination parameters
 * @returns Page response containing subcategory descriptors
 * @throws Error when backend validation fails or response schema mismatches
 * @example
 * ```ts
 * const page = await listIndirectCostSubcategories({ category: 'OPEX', active: true })
 * const first = page.content.at(0)
 * ```
 */
export async function listIndirectCostSubcategories(
  params: ListIndirectCostSubcategoriesParams = {}
): Promise<PageIndirectCostSubcategoryResponse> {
  const response = await apiClient.get('/indirect-costs/subcategories', {
    params: {
      category: params.category,
      search: params.search,
      page: params.page ?? 0,
      size: params.size ?? 200,
    },
  })
  const parsed = pageIndirectCostSubcategoryResponseSchema.safeParse(response.data)
  if (!parsed.success) {
    throw new Error('API validation failed for GET /indirect-costs/subcategories')
  }
  return parsed.data
}

export interface CreateIndirectCostSubcategoryRequest {
  displayName: string
  category: 'OPEX' | 'DA'
  slug?: string
}

export async function createIndirectCostSubcategory(
  body: CreateIndirectCostSubcategoryRequest
): Promise<IndirectCostSubcategoryDTO> {
  const response = await apiClient.post('/indirect-costs/subcategories', body)
  const parsed = indirectCostSubcategorySchema.safeParse(response.data)
  if (!parsed.success) {
    throw new Error('API validation failed for POST /indirect-costs/subcategories')
  }
  return parsed.data
}

export interface UpdateIndirectCostSubcategoryRequest {
  displayName?: string
  slug?: string
  autoCreateMonthly?: boolean
}

export async function updateIndirectCostSubcategory(
  uuid: string,
  body: UpdateIndirectCostSubcategoryRequest
): Promise<IndirectCostSubcategoryDTO> {
  const response = await apiClient.put(`/indirect-costs/subcategories/${uuid}`, body)
  const parsed = indirectCostSubcategorySchema.safeParse(response.data)
  if (!parsed.success) {
    throw new Error('API validation failed for PUT /indirect-costs/subcategories/{uuid}')
  }
  return parsed.data
}

export async function deleteIndirectCostSubcategory(uuid: string): Promise<void> {
  await apiClient.delete(`/indirect-costs/subcategories/${uuid}`)
}

export const indirectCostSubcategoriesService = {
  listIndirectCostSubcategories,
  createIndirectCostSubcategory,
  updateIndirectCostSubcategory,
  deleteIndirectCostSubcategory,
}

export type { IndirectCostSubcategoryDTO }

export default indirectCostSubcategoriesService
